import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Search, X } from 'lucide-react';
import type { Asset } from '@hcc/types';
import { Button } from '../../components/ui/Button';
import { ApiError } from '../../lib/api';
import { useAssignAsset } from '../../hooks/useAssets';
import { usePeopleList } from '../../hooks/usePeople';

/**
 * Modal for picking a person to pre-assign an asset to. Re-uses the
 * existing /api/people search endpoint via usePeopleList. Bones flow —
 * coordinator types, picks, confirms, done.
 */
export function AssignAssetDialog({
  asset,
  onClose,
}: {
  asset: Asset;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<{ id: string; full_name: string } | null>(
    null,
  );
  const list = usePeopleList(q);
  const assign = useAssignAsset();

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  async function confirm() {
    if (!picked) return;
    try {
      await assign.mutateAsync({ id: asset.id, person_id: picked.id });
      toast.success(`Assigned ${asset.asset_tag} to ${picked.full_name}`);
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'assign failed');
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/50 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 -z-10"
      />
      <div className="animate-fade-in flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-surface shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-line/10 px-4 py-3">
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-fg-muted">
              Pre-assign
            </div>
            <h2 className="text-lg font-semibold tracking-tight">
              {asset.asset_tag}
            </h2>
            <p className="text-xs text-fg-muted">{asset.description}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<X className="h-4 w-4" />}
            onClick={onClose}
            aria-label="Close"
          >
            Close
          </Button>
        </div>

        <div className="border-b border-line/10 px-4 py-3">
          <div className="relative">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-dim"
            />
            <input
              autoFocus
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search people by name or email..."
              className="block h-9 w-full rounded-md border border-line-strong/20 bg-surface pl-8 pr-3 text-sm shadow-sm focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-strong focus-visible:ring-offset-1"
            />
          </div>
        </div>

        <div className="min-h-[12rem] flex-1 overflow-y-auto">
          {list.isLoading ? (
            <div className="px-4 py-3 text-sm text-fg-muted">Loading...</div>
          ) : list.error ? (
            <div className="px-4 py-3 text-sm text-danger-soft-fg">
              {(list.error as Error).message}
            </div>
          ) : !list.data || list.data.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-fg-muted">
              {q.length === 0 ? 'Type to search.' : 'No matches.'}
            </div>
          ) : (
            <ul className="divide-y divide-divider/10">
              {list.data.slice(0, 30).map((p) => {
                const isPicked = picked?.id === p.id;
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => setPicked({ id: p.id, full_name: p.full_name })}
                      className={[
                        'flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm transition-colors',
                        isPicked ? 'bg-surface-2' : 'hover:bg-surface-2',
                      ].join(' ')}
                    >
                      <div className="min-w-0">
                        <div className="truncate font-medium">{p.full_name}</div>
                        <div className="truncate text-xs text-fg-muted">
                          {p.email}
                          {p.position ? ` · ${p.position}` : ''}
                        </div>
                      </div>
                      {isPicked ? (
                        <span className="rounded-full bg-slate-900 px-2 py-0.5 text-[11px] font-medium text-white">
                          Picked
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-line/10 px-4 py-3">
          <div className="min-w-0 text-xs text-fg-muted">
            {picked ? (
              <>
                Assigning to{' '}
                <span className="font-medium text-fg">
                  {picked.full_name}
                </span>
              </>
            ) : (
              'Pick a person to assign.'
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={assign.isPending}
              disabled={!picked || assign.isPending}
              onClick={confirm}
            >
              Confirm
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
